import { Directive, NgZone, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { Calendar } from 'primeng/calendar';

/**
 * Desactiva que el p-calendar se abra al recibir el foco (evita apertura automática
 * en p-dialog), pero mantiene la apertura al hacer click en el campo.
 *
 * PrimeNG condiciona onInputClick a showOnFocus, por lo que es necesario sobrescribir
 * el método para que el click funcione independientemente de showOnFocus.
 *
 * Además reposiciona el panel cuando se hace scroll de la ventana. Con appendTo="body"
 * PrimeNG posiciona el panel con DomHandler.absolutePosition (top = rect.top + scrollTop),
 * es decir en coordenadas de body, mientras que dentro de un diálogo el input es fixed y
 * no se mueve: al hacer scroll el panel se despegaba del campo. El listener propio de
 * PrimeNG (ConnectedOverlayScrollHandler) no cubre este caso porque solo escucha en los
 * ancestros DOM con overflow, nunca en window.
 */
@Directive({
    selector: 'p-calendar',
    standalone: false
})
export class CalendarNoShowOnFocusDirective implements OnInit, OnDestroy {
    private subscriptions = new Subscription();
    private scrollListener?: () => void;
    private realignRequest?: number;

    constructor(private calendar: Calendar, private zone: NgZone) {
        this.calendar.showOnFocus = false;
    }

    ngOnInit(): void {
        this.calendar.onInputClick = () => {
            if (!this.calendar.overlayVisible) {
                this.calendar.showOverlay();
            }
        };

        this.subscriptions.add(this.calendar.onShow.subscribe(() => this.bindWindowScrollListener()));
        this.subscriptions.add(this.calendar.onClose.subscribe(() => this.unbindWindowScrollListener()));
    }

    ngOnDestroy(): void {
        this.unbindWindowScrollListener();
        this.subscriptions.unsubscribe();
    }

    /** Solo hace falta con appendTo: sin él el panel vive dentro del contenedor y ya acompaña al campo. */
    private bindWindowScrollListener(): void {
        if (!this.calendar.appendTo || this.scrollListener) {
            return;
        }

        this.zone.runOutsideAngular(() => {
            const onScroll = () => this.scheduleRealign();
            window.addEventListener('scroll', onScroll, { passive: true });
            this.scrollListener = () => window.removeEventListener('scroll', onScroll);
        });
    }

    private unbindWindowScrollListener(): void {
        if (this.realignRequest) {
            cancelAnimationFrame(this.realignRequest);
            this.realignRequest = undefined;
        }

        this.scrollListener?.();
        this.scrollListener = undefined;
    }

    /** Agrupa los eventos de scroll en un frame: alignOverlay mide y escribe estilos. */
    private scheduleRealign(): void {
        if (this.realignRequest) {
            return;
        }

        this.realignRequest = requestAnimationFrame(() => {
            this.realignRequest = undefined;

            if (this.calendar.overlayVisible) {
                this.calendar.alignOverlay();
            }
        });
    }
}
